import {
  Button,
  Divider,
  Heading,
  HStack,
  Stack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { StyledScrollWrapper } from '$/components/common/StyledScrollWrapper';
import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { useIsMobile } from '$/hooks/useIsMobile';
import { InviteTeacherModal } from '$/pages/SchoolPage/components/InviteTeacherModal';
import { SchoolProfileSection } from '$/pages/SchoolPage/components/SchoolProfileSection';
import { TeacherCardList } from '$/pages/SchoolPage/components/TeacherCardList';
import { TeacherInvitationCardList } from '$/pages/SchoolPage/components/TeacherInvitationCardList';
import { teacherInvitationsQuery } from '$/services/usecases/invitation/queries';
import {
  schoolQuery,
  schoolTeachersQuery,
} from '$/services/usecases/school/queries';

export const SchoolPage = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { hasPermission } = useAuthorization();
  const isMobile = useIsMobile();
  const user = useAuthenticationStore.useUser();
  const inviteModal = useDisclosure();
  const schoolProfileSection = useDisclosure();

  const { data: school } = useQuery(schoolQuery(user?.schoolId));
  const { data: teachers } = useQuery(schoolTeachersQuery(user?.schoolId));
  const { data: teacherInvitations } = useQuery({
    ...teacherInvitationsQuery,
    enabled: hasPermission('School_Manage_Teachers'),
  });

  return (
    <Stack ref={ref} layerStyle='DashboardSection' w='full'>
      <StyledScrollWrapper>
        <Stack
          gap='6'
          overflowY='auto'
          w='full'
          {...(!isMobile && {
            p: '7',
          })}
        >
          <VStack align='start'>
            <HStack justify='space-between'>
              <Heading as='h1' fontSize='3xl'>
                {school?.response.payload.name}
              </Heading>
            </HStack>
            <Button
              color='primaryButton.background'
              onClick={schoolProfileSection.onToggle}
              size='sm'
              variant='link'
            >
              {schoolProfileSection.isOpen
                ? t('school.profile.hide')
                : t('school.profile.show')}
            </Button>
            {schoolProfileSection.isOpen && (
              <SchoolProfileSection
                school={school?.response.payload}
                teachers={teachers?.response.payload}
              />
            )}
          </VStack>
          <HStack justify='space-between'>
            <Heading as='h2' fontSize='2xl'>
              {t('school.teachers.title')}
            </Heading>
            {hasPermission('School_Manage_Teachers') && (
              <Button h='10' fontSize='sm' onClick={inviteModal.onOpen}>
                {t('school.teachers.inviteTeacherButton')}
              </Button>
            )}
          </HStack>
          <TeacherCardList teachers={teachers?.response.payload ?? []} />
          {hasPermission('School_Manage_Teachers') && (
            <>
              <Divider borderColor='border' />
              <Heading as='h2' fontSize='2xl'>
                {t('school.teachers.invitationsTitle')}
              </Heading>
              <TeacherInvitationCardList
                teacherInvitations={teacherInvitations?.response ?? []}
              />
            </>
          )}
        </Stack>
      </StyledScrollWrapper>
      {user?.schoolId && (
        <InviteTeacherModal {...inviteModal} schoolId={user.schoolId} />
      )}
    </Stack>
  );
};
